import type {
  IInfoPanelGroup,
  IInfoPanelItem,
  IInfoPanelItemData,
  IInfoPanelSearchOptions,
} from '../types';

import { useState, useMemo, useCallback } from 'react';

const matchesSearch = (item: IInfoPanelItem, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  const labelText = typeof item.label === 'string' ? item.label.toLowerCase() : '';

  if (labelText.includes(normalizedQuery)) return true;

  return item.tags?.some((tag) => tag.toLowerCase() === normalizedQuery) ?? false;
};

interface UseGInfoPanelProps {
  items: IInfoPanelItem[];
  groups: IInfoPanelGroup[];
  selectedId?: string;
  defaultInfo?: IInfoPanelItemData;
  search?: IInfoPanelSearchOptions;
  onSelect?: (id: string) => void;
}

export const useGInfoPanel = ({
  items,
  groups,
  selectedId,
  defaultInfo,
  search,
  onSelect,
}: UseGInfoPanelProps) => {
  const [internalSelectedId, setInternalSelectedId] = useState<string | undefined>(selectedId);
  const [hoveredId, setHoveredId] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');

  const currentSelectedId = selectedId ?? internalSelectedId;

  const allItems = useMemo(() => {
    const itemsList = [...items];
    groups.forEach((group) => {
      itemsList.push(...group.items);
    });
    return itemsList;
  }, [items, groups]);

  const filteredItems = useMemo(() => {
    if (!searchQuery || !search?.enabled) return items;

    return items.filter((item) => matchesSearch(item, searchQuery));
  }, [items, searchQuery, search?.enabled]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery || !search?.enabled) return groups;

    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => matchesSearch(item, searchQuery)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, searchQuery, search?.enabled]);

  const currentInfo = useMemo(() => {
    const displayId = hoveredId ?? currentSelectedId;

    if (displayId) {
      const item = allItems.find((i) => i.id === displayId);
      return item?.data;
    }

    return defaultInfo;
  }, [hoveredId, currentSelectedId, allItems, defaultInfo]);

  const handleSelect = useCallback(
    (id: string) => {
      setInternalSelectedId(id);
      onSelect?.(id);
    },
    [onSelect],
  );

  const handleHover = useCallback((id: string) => {
    setHoveredId((prevId) => (prevId === id ? prevId : id));
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setHoveredId(undefined);
      setSearchQuery(value);
      search?.onSearch?.(value);
    },
    [search],
  );

  const handleClearSearch = useCallback(() => {
    handleSearchChange('');
  }, [handleSearchChange]);

  const hasNoResults = useMemo(() => {
    if (!searchQuery || !search?.enabled) return false;
    return filteredItems.length === 0 && filteredGroups.length === 0;
  }, [searchQuery, search?.enabled, filteredItems, filteredGroups]);

  return {
    currentSelectedId,
    searchQuery,
    filteredItems,
    filteredGroups,
    currentInfo,
    hasNoResults,
    handleSelect,
    handleHover,
    handleSearchChange,
    handleClearSearch,
  };
};
